import { Card, Collapse, Empty, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import Buttons from 'components/Global/Buttons';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import SearchComponent from 'components/Global/SearchComponent';
import SelectDropdown from 'components/Global/SelectDropdown';
import styles from './index.module.scss';
import Ellipse from 'components/Global/Ellipse';

const { Text, Link } = Typography;

export type FamilyMemberStatus = 'Alive' | 'Deceased';
export type FamilyHistoryEventType = 'added' | 'status_changed';

export interface FamilyHistoryMember {
	id: string;
	name: string;
	relation: string;
	family_side?: 'maternal' | 'paternal' | 'none';
	is_next_of_kin?: boolean;
	mobile?: string | null;
	email?: string | null;
	date_of_birth: string;
	current_status: FamilyMemberStatus;
	updated_at: string;
	created_at: string;
	created_by?: string;
	created_by_name?: string;
	history?: FamilyHistoryEvent[];
}

export interface FamilyHistoryEvent {
	id: string;
	event_type: FamilyHistoryEventType;
	previous_status?: FamilyMemberStatus | null;
	new_status?: FamilyMemberStatus | null;
	effective_date?: string | null;
	notes?: string | null;
	created_at: string;
	changed_by?: string;
	changed_by_name?: string;
}

type Props = {
	readOnly?: boolean;
	reloadKey?: number;
	onAddMember?: () => void;
	onChangeStatus?: (member: FamilyHistoryMember) => void;
	fetchMembers: (params: {
		search?: string;
		status?: 'Alive' | 'Deceased' | 'all';
		sort?: 'most_recent';
		limit: number;
		offset: number;
	}) => Promise<{ data: FamilyHistoryMember[]; total: number }>;
	fetchHistory?: (
		memberId: string,
	) => Promise<{ data: FamilyHistoryEvent[]; total: number }>;
};

const PAGE_SIZE = 20;

const getRelationLabel = (relation: string) =>
	relation === 'SpouseDivorced' ? 'Spouse (Divorced)' : relation;

const getSideLabel = (side?: 'maternal' | 'paternal' | 'none') => {
	if (side === 'maternal') return 'Maternal';
	if (side === 'paternal') return 'Paternal';
	return null;
};

const TimeLineBaseUI = (props: Props) => {
	const {
		readOnly = false,
		reloadKey = 0,
		onAddMember,
		onChangeStatus,
		fetchMembers,
		fetchHistory,
	} = props;

	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search, 500);
	const [status, setStatus] = useState<'Alive' | 'Deceased' | 'all'>('all');
	const [members, setMembers] = useState<FamilyHistoryMember[]>([]);
	const [total, setTotal] = useState(0);
	const [offset, setOffset] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [historyMap, setHistoryMap] = useState<
		Record<string, FamilyHistoryEvent[]>
	>({});
	const [historyLoadingMap, setHistoryLoadingMap] = useState<
		Record<string, boolean>
	>({});
	const [activeKeys, setActiveKeys] = useState<string[]>([]);
	const listRef = useRef<HTMLDivElement>(null);

	const canLoadMore = members.length < total;

	const loadMembers = async (reset = false) => {
		if (reset) {
			setIsLoading(true);
		} else {
			setIsLoadingMore(true);
		}
		try {
			const nextOffset = reset ? 0 : offset;
			const result = await fetchMembers({
				search: debouncedSearch || undefined,
				status,
				sort: 'most_recent',
				limit: PAGE_SIZE,
				offset: nextOffset,
			});
			setTotal(result.total || 0);
			const pageData = result.data || [];

			if (reset) {
				const embeddedHistory: Record<string, FamilyHistoryEvent[]> = {};
				pageData.forEach((m) => {
					if (m.history?.length) {
						embeddedHistory[m.id] = m.history;
					}
				});
				setHistoryMap(embeddedHistory);
			}

			setMembers((prev) => {
				const nextMembers = reset ? pageData : [...prev, ...pageData];

				if (reset && activeKeys.length > 0) {
					const availableMemberIds = new Set(
						nextMembers.map((item) => item.id),
					);
					const nextActiveKeys = activeKeys.filter((key) =>
						availableMemberIds.has(key),
					);
					setActiveKeys(nextActiveKeys);
					nextActiveKeys.forEach((id) => {
						const row = nextMembers.find((m) => m.id === id);
						if (!row?.history?.length) {
							loadHistory(id);
						}
					});
				}

				return nextMembers;
			});
			setOffset(nextOffset + PAGE_SIZE);
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	};

	useEffect(() => {
		setOffset(0);
		setMembers([]);
		setHistoryMap({});
		setHistoryLoadingMap({});
		loadMembers(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, reloadKey]);

	useEffect(() => {
		setOffset(0);
		setMembers([]);
		setHistoryMap({});
		setHistoryLoadingMap({});
		loadMembers(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	const loadHistory = async (memberId: string) => {
		if (historyMap[memberId] || historyLoadingMap[memberId]) return;
		const existingMember = members.find((member) => member.id === memberId);
		if (existingMember?.history) {
			setHistoryMap((prev) => ({
				...prev,
				[memberId]: existingMember.history || [],
			}));
			return;
		}
		if (!fetchHistory) {
			setHistoryMap((prev) => ({ ...prev, [memberId]: [] }));
			return;
		}
		setHistoryLoadingMap((prev) => ({ ...prev, [memberId]: true }));
		try {
			const result = await fetchHistory(memberId);
			setHistoryMap((prev) => ({ ...prev, [memberId]: result.data || [] }));
		} finally {
			setHistoryLoadingMap((prev) => ({ ...prev, [memberId]: false }));
		}
	};

	const handleScroll = () => {
		if (!listRef.current || isLoading || isLoadingMore || !canLoadMore) return;
		const { scrollTop, scrollHeight, clientHeight } = listRef.current;
		if (scrollHeight - scrollTop - clientHeight < 100) {
			loadMembers(false);
		}
	};

	const collapseItems = useMemo(
		() =>
			members.map((member) => ({
				key: member.id,
				label: (
					<Space>
						<Text strong>{member.name}</Text>
						<Text type="secondary">({getRelationLabel(member.relation)})</Text>
						{getSideLabel(member.family_side) && (
							<Tag color="blue">{getSideLabel(member.family_side)}</Tag>
						)}
						{member.is_next_of_kin && <Tag color="purple">Next of Kin</Tag>}
						<Tag color={member.current_status === 'Alive' ? 'green' : 'red'}>
							{member.current_status}
						</Tag>
					</Space>
				),
				extra: !readOnly ? (
					<div onMouseDown={(event) => event.stopPropagation()}>
						<Link
							onClick={(event) => {
								event.stopPropagation();
								onChangeStatus?.(member);
							}}
						>
							Change Status
						</Link>
					</div>
				) : null,
				children: historyLoadingMap[member.id] ? (
					<Spin />
				) : (historyMap[member.id] || []).length === 0 ? (
					<Empty
						description="No history found"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				) : (
					<div>
						{(historyMap[member.id] || []).map((event) => (
							<div
								key={event.id}
								className={`${styles.historyItem} ${
									event.event_type === 'added'
										? styles.eventAdded
										: styles.eventChanged
								}`}
							>
								<div className={styles.historyHeader}>
									<Text strong>
										{event.event_type === 'added'
											? 'Member added'
											: `Status changed from ${event.previous_status} to ${event.new_status}`}
									</Text>
									<Text className={styles.metaText}>
										{dayjs(event.created_at).format('DD MMM YYYY')}
									</Text>
								</div>
								{event.event_type === 'status_changed' && (
									<>
										{event.effective_date && (
											<Text className={styles.metaText}>
												Effective date:{' '}
												{dayjs(event.effective_date).format('DD MMM YYYY')}
											</Text>
										)}
										{event.notes && (
											<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
												<span>Reason:</span>
												<Ellipse
													message={event.notes}
													maxLength={20}
													tooltipMessage={event.notes}
													isTooltip={true}
												/>
											</div>
										)}
									</>
								)}
								{event.event_type === 'added' &&
									(member.date_of_birth ||
										getSideLabel(member.family_side) ||
										member.is_next_of_kin) && (
										<div className={styles.addedEventDetails}>
											{member.date_of_birth && (
												<div className={styles.detailRow}>
													<span className={styles.detailLabel}>
														Date of birth
													</span>
													<span className={styles.detailValue}>
														{dayjs(member.date_of_birth).format(
															'DD MMM YYYY',
														)}
													</span>
												</div>
											)}
											{member.is_next_of_kin && (
												<div className={styles.nextOfKinBlock}>
													<Text className={styles.nextOfKinTitle}>
														Next of kin
													</Text>
													{member.mobile && (
														<div className={styles.detailRow}>
															<span className={styles.detailLabel}>
																Mobile
															</span>
															<span className={styles.detailValue}>
																{member.mobile}
															</span>
														</div>
													)}
													{member.email && (
														<div className={styles.detailRow}>
															<span className={styles.detailLabel}>
																Email
															</span>
															<span className={styles.detailValue}>
																{member.email}
															</span>
														</div>
													)}
												</div>
											)}
										</div>
									)}
								<Text className={styles.metaText}>
									{event.event_type === 'added' ? 'Added by: ' : 'Changed by: '}
									{event.changed_by_name || 'N/A'}
								</Text>
							</div>
						))}
					</div>
				),
			})),
		[historyLoadingMap, historyMap, members, onChangeStatus, readOnly],
	);

	return (
		<div className={styles.container}>
			<div className={styles.controls}>
				<div className={styles.searchContainer}>
					<SearchComponent
						placeHolder="Search by member name"
						className={styles.searchInput}
						value={search}
						handleChange={(value) => setSearch(value)}
						handleChangeApi={() => {}}
					/>
					<SelectDropdown
						value={status}
						onChange={(value) =>
							setStatus(value as 'Alive' | 'Deceased' | 'all')
						}
						options={[
							{ value: 'all', label: 'All status' },
							{ value: 'Alive', label: 'Alive' },
							{ value: 'Deceased', label: 'Deceased' },
						]}
						defaultOption={false}
						placeholder="Select Status"
						required={false}
						helperText=""
						width="200px"
						isError={false}
						size="large"
						label=""
					/>
				</div>
				{!readOnly && (
					<Buttons
						buttons={[
							{
								text: 'Add Member',
								isLoading: false,
								className: 'btn-blue',
								isSubmit: false,
								disabled: false,
								onclick: () => onAddMember?.(),
								minWidth: '14rem',
							},
						]}
					/>
				)}
			</div>

			<Card bordered={false}>
				{isLoading ? (
					<div className={styles.footerLoader}>
						<Spin />
					</div>
				) : members.length === 0 ? (
					<Empty description="No family members found" />
				) : (
					<div
						className={styles.listContainer}
						ref={listRef}
						onScroll={handleScroll}
					>
						<Collapse
							className={styles.memberCard}
							activeKey={activeKeys}
							onChange={(keys) => {
								const normalized = (Array.isArray(keys) ? keys : [keys]).map(
									(item) => String(item),
								);
								setActiveKeys(normalized);
								normalized.forEach((id) => loadHistory(id));
							}}
							items={collapseItems}
						/>
						{isLoadingMore && (
							<div className={styles.footerLoader}>
								<Spin />
							</div>
						)}
					</div>
				)}
			</Card>
		</div>
	);
};

export default TimeLineBaseUI;
